import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ActionIcon, Alert, Button, Card, Group, Text, Tooltip } from '@mantine/core';
import { IconTrash } from '@tabler/icons-react';
import {
  useDealRowRepository,
  useDealVersionRepository,
  useWorkOrderRepository,
  partnerRepository,
} from '@ayocore/erp/crm/client/rpc';
import type { CreateDealRow, UpdateDealRow } from '@ayocore/erp/crm/schemas/deal-row.schema';
import {
  EditableGrid,
  CheckboxCell,
  MoneyCell,
  NumberCell,
  parseNullableNumber,
  SuggestionCell,
  toCellDisplay,
} from '@ayocore/platform/client/components/EditableGrid';
import type {
  EditableGridColumn,
  EditableGridSuggestOption,
} from '@ayocore/platform/client/components/EditableGrid';
import { createProductSuggestionColumn } from '@ayocore/catalog/client/components/EditableGridColumns';
import type { ProductSource } from '@ayocore/catalog/client/components/EditableGridColumns';
import { useModal } from '@ayocore/platform/client/hooks/useModal';
import {
  ProductPicker,
  getProductMetadataImages,
} from '@ayocore/erp/crm/client/components/ProductPicker';
import type {
  ProductRowImageMetadata,
  SelectedProductItem,
} from '@ayocore/erp/crm/client/components/ProductPicker';
import { DealRowWorkOrdersBlock } from '@ayocore/erp/crm/client/components/WorkOrders/DealRowWorkOrdersBlock';
import { DealRowNotes } from './DealRowNotes';

type DealRowMetadata = {
  images?: ProductRowImageMetadata[] | null;
};

type DealRowsGridRow = ProductSource & {
  id?: number;
  product_name?: string | null;
  product_supplier?: string | null;
  supplier_id?: number | null;
  quantity?: number | null;
  price?: number | null;
  purchase_price?: number | null;
  amount?: number | null;
  expenses_amount?: number | null;
  discount?: number | null;
  total?: number | null;
  sort_order?: number | null;
  metadata?: DealRowMetadata | null;
  notes?: string | null;
  approved_at?: string | null;
};

type DealRowsGridColumnKey =
  | 'product'
  | 'supplier'
  | 'quantity'
  | 'price'
  | 'purchase_price'
  | 'amount'
  | 'expenses_amount'
  | 'approved';
/** Патч строки: UpdateDealRow, при этом product_id/product_name могут быть null (колонка «Товар»). */
type DealRowsGridPatch = Omit<
  Partial<UpdateDealRow>,
  'product_id' | 'product_name' | 'approved_at'
> & {
  product_id?: number | null;
  product_name?: string | null;
  product?: null;
  supplier_id?: number | null;
  approved_at?: string | null;
};

type SupplierSearchRow = {
  id?: number;
  name?: string | null;
  full_name?: string | null;
  inn?: string | null;
};

type DealRowsTotals = {
  amount: number;
  discount: number;
  total: number;
};

const EMPTY_TOTALS: DealRowsTotals = {
  amount: 0,
  discount: 0,
  total: 0,
};

type WorkOrderExpenseRow = {
  deal_row_id?: number | null;
  qty?: number | null;
  price?: number | null;
  amount?: number | null;
};

type DealRowsExpensesByRowId = Record<number, number>;

function roundMoney(value: number): number {
  return Math.round(value * 100) / 100;
}

function calculateWorkOrderAmount(row: WorkOrderExpenseRow): number {
  const amount = Number(row.amount);
  if (Number.isFinite(amount)) return roundMoney(amount);
  return roundMoney(Number(row.qty ?? 0) * Number(row.price ?? 0));
}

function calculateExpensesByDealRow(workOrders: WorkOrderExpenseRow[]): DealRowsExpensesByRowId {
  return workOrders.reduce<DealRowsExpensesByRowId>((acc, workOrder) => {
    const dealRowId = Number(workOrder.deal_row_id ?? 0);
    if (!Number.isFinite(dealRowId) || dealRowId <= 0) return acc;
    acc[dealRowId] = roundMoney((acc[dealRowId] ?? 0) + calculateWorkOrderAmount(workOrder));
    return acc;
  }, {});
}

function calculateRowAmount(row: Pick<DealRowsGridRow, 'quantity' | 'price'>): number {
  return roundMoney(Number(row.quantity ?? 0) * Number(row.price ?? 0));
}

function calculateRowsTotals(rows: DealRowsGridRow[]): DealRowsTotals {
  return rows.reduce<DealRowsTotals>(
    (acc, row) => {
      const amount = calculateRowAmount(row);
      const discount = Number(row.discount ?? 0);
      const total = Number(row.total ?? amount - discount);
      acc.amount += amount;
      acc.discount += discount;
      acc.total += total;
      return acc;
    },
    { ...EMPTY_TOTALS }
  );
}

function getDealRowSupplierName(row: DealRowsGridRow): string {
  return row.product_supplier ?? '';
}

function mapSupplierRowsToSuggestOptions(rows: SupplierSearchRow[]): EditableGridSuggestOption[] {
  return (rows ?? [])
    .filter(item => item.id != null)
    .map(item => ({
      id: String(item.id),
      label: item.name ?? item.full_name ?? `ID ${item.id}`,
      hint: item.inn ?? undefined,
      payload: item,
    }));
}

async function searchSuppliers(query: string): Promise<EditableGridSuggestOption[]> {
  const rows = (await partnerRepository.search(query).limit(8).fetch()) as SupplierSearchRow[];
  return mapSupplierRowsToSuggestOptions(rows ?? []);
}

function calculateNewSortOrder(rows: DealRowsGridRow[], newIndex: number): number {
  const STEP = 1000;
  const BASE = 1000;

  const rawPrev = newIndex > 0 ? (rows[newIndex - 1]?.sort_order ?? null) : null;
  const rawNext = newIndex < rows.length - 1 ? (rows[newIndex + 1]?.sort_order ?? null) : null;

  const prev = rawPrev != null && Number(rawPrev) !== 0 ? Number(rawPrev) : null;
  const next = rawNext != null && Number(rawNext) !== 0 ? Number(rawNext) : null;

  if (prev != null && next != null) {
    return prev === next ? prev + STEP : (prev + next) / 2;
  }
  if (prev != null) {
    return prev + STEP;
  }
  if (next != null) {
    return next > 0 ? next / 2 : BASE;
  }
  return BASE;
}

function normalizeBaseUrl(value: string): string {
  return value.replace(/\/+$/, '');
}

function buildProposalViewerLink(dealUuid: string): string {
  const envBase =
    typeof import.meta.env.VITE_PROPOSAL_VIEWER_BASE_URL === 'string'
      ? import.meta.env.VITE_PROPOSAL_VIEWER_BASE_URL.trim()
      : '';
  const base = envBase || window.location.origin;
  return `${normalizeBaseUrl(base)}/p/${encodeURIComponent(dealUuid)}`;
}

async function copyTextToClipboard(text: string): Promise<void> {
  if (navigator.clipboard?.writeText) {
    await navigator.clipboard.writeText(text);
    return;
  }

  const textarea = document.createElement('textarea');
  textarea.value = text;
  textarea.setAttribute('readonly', 'true');
  textarea.style.position = 'absolute';
  textarea.style.left = '-9999px';
  document.body.appendChild(textarea);
  textarea.select();

  try {
    const copied = document.execCommand('copy');
    if (!copied) {
      throw new Error('Не удалось скопировать ссылку на КП');
    }
  } finally {
    document.body.removeChild(textarea);
  }
}

/**
 * DealRowsPanel — строки сделки на базе EditableGrid.
 */
export function DealRowsPanel({ dealId, dealUuid }: { dealId: number; dealUuid?: string | null }) {
  const dealVersionRepo = useDealVersionRepository();
  const dealRowRepo = useDealRowRepository();
  const workOrderRepo = useWorkOrderRepository();
  const { open: openProductPickerModal, close: closeProductPickerModal } = useModal(ProductPicker);

  const dealVersionRepoRef = useRef(dealVersionRepo);
  const dealRowRepoRef = useRef(dealRowRepo);
  const workOrderRepoRef = useRef(workOrderRepo);
  const rowsCacheRef = useRef<DealRowsGridRow[]>([]);

  const [version, setVersion] = useState<{ id: number; version: string } | null>(null);
  const [totals, setTotals] = useState<DealRowsTotals>(EMPTY_TOTALS);
  const [error, setError] = useState<string | null>(null);
  const [gridRevision, setGridRevision] = useState(0);
  const [shareCopied, setShareCopied] = useState(false);
  const shareResetTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    dealVersionRepoRef.current = dealVersionRepo;
  }, [dealVersionRepo]);

  useEffect(() => {
    dealRowRepoRef.current = dealRowRepo;
  }, [dealRowRepo]);

  useEffect(() => {
    workOrderRepoRef.current = workOrderRepo;
  }, [workOrderRepo]);

  const [expensesByDealRowId, setExpensesByDealRowId] = useState<DealRowsExpensesByRowId>({});

  /** Загружает расходы по work orders для всех указанных deal row id. */
  const loadExpensesForRows = useCallback(async (dealRowIds: number[]) => {
    if (!dealRowIds.length) {
      setExpensesByDealRowId({});
      return;
    }
    const workOrders = (await workOrderRepoRef.current
      .filterByDealRows(dealRowIds)
      .fetch()) as WorkOrderExpenseRow[];
    setExpensesByDealRowId(calculateExpensesByDealRow(workOrders ?? []));
  }, []);

  /** Перезагружает расходы для одной deal row (после изменения work orders). */
  const refreshExpensesForDealRow = useCallback(async (dealRowId: number) => {
    const workOrders = (await workOrderRepoRef.current
      .filterByDealRows([dealRowId])
      .fetch()) as WorkOrderExpenseRow[];
    const freshExpenses = calculateExpensesByDealRow(workOrders ?? []);
    setExpensesByDealRowId(prev => {
      const next = { ...prev };
      delete next[dealRowId];
      return { ...next, ...freshExpenses };
    });
  }, []);

  useEffect(() => {
    return () => {
      if (shareResetTimerRef.current) {
        clearTimeout(shareResetTimerRef.current);
      }
    };
  }, []);

  const columns = useMemo<
    EditableGridColumn<DealRowsGridRow, DealRowsGridPatch, DealRowsGridColumnKey>[]
  >(
    () => [
      createProductSuggestionColumn<DealRowsGridRow>({
        width: 300,
      }),
      SuggestionCell<DealRowsGridRow, DealRowsGridPatch, DealRowsGridColumnKey>({
        key: 'supplier',
        title: 'Поставщик',
        width: 170,
        placeholder: 'Поиск поставщика...',
        minChars: 2,
        getDisplayFromRow: row =>
          toCellDisplay(getDealRowSupplierName(row), { linked: row.supplier_id != null }),
        getEditorFromRow: row => ({
          value: getDealRowSupplierName(row),
          meta: { selectedRef: row.supplier_id ?? null },
        }),
        getSelectedRefFromRow: row => row.supplier_id ?? null,
        onSearch: searchSuppliers,
        onPatch: ({ row, value, selectedRef }) => {
          const typedName = value.trim();
          const nextSupplierId = typeof selectedRef === 'number' ? selectedRef : null;
          const nextSupplierName = typedName || null;
          const prevSupplierName = row.product_supplier ?? null;
          const prevSupplierId = row.supplier_id ?? null;

          return {
            hasChanges: nextSupplierName !== prevSupplierName || nextSupplierId !== prevSupplierId,
            apiPatch: {
              product_supplier: nextSupplierName,
              supplier_id: nextSupplierId,
            },
            localPatch: {
              product_supplier: nextSupplierName,
              supplier_id: nextSupplierId,
            },
          };
        },
      }),
      NumberCell<DealRowsGridRow, DealRowsGridPatch, DealRowsGridColumnKey>({
        key: 'quantity',
        title: 'Кол-во',
        width: 80,
        fallback: 1,
        precision: 0,
        minValue: 0,
      }),
      MoneyCell<DealRowsGridRow, DealRowsGridPatch, DealRowsGridColumnKey>({
        key: 'price',
        title: 'Цена продажи',
        width: 100,
        fallback: 0,
        precision: 2,
        minValue: 0,
      }),
      {
        key: 'amount' as DealRowsGridColumnKey,
        title: 'Сумма продажи',
        width: 150,
        kind: 'money' as const,
        editable: false,
        persist: true,
        value: (row: DealRowsGridRow) =>
          roundMoney(Number(row.quantity ?? 0) * Number(row.price ?? 0)),
        getDisplayFromRow: (row: DealRowsGridRow) => {
          const amount = roundMoney(Number(row.quantity ?? 0) * Number(row.price ?? 0));
          const rowId = Number(row.id ?? 0);
          const purchaseTotal = roundMoney(
            Number(row.purchase_price ?? 0) * Number(row.quantity ?? 0)
          );
          const workOrdersTotal = rowId > 0 ? (expensesByDealRowId[rowId] ?? 0) : 0;
          const expenses = roundMoney(purchaseTotal + workOrdersTotal);
          const amountText =
            amount === 0
              ? '0,00'
              : amount.toLocaleString(undefined, {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2,
                });
          if (amount <= 0 || expenses <= 0) {
            return toCellDisplay(amountText);
          }
          const margin = roundMoney(((amount - expenses) / amount) * 100);
          const marginStr = margin < 30 ? `⚠ ${margin.toFixed(0)}%` : `${margin.toFixed(0)}%`;
          return toCellDisplay(`${amountText}  ${marginStr}`);
        },
        getEditorFromRow: (row: DealRowsGridRow) => {
          const amount = roundMoney(Number(row.quantity ?? 0) * Number(row.price ?? 0));
          return { value: amount.toFixed(2) };
        },
        buildRowPatch: () => ({ hasChanges: false, apiPatch: {} as DealRowsGridPatch }),
      },
      MoneyCell<DealRowsGridRow, DealRowsGridPatch, DealRowsGridColumnKey>({
        key: 'purchase_price',
        title: 'Цена закуп',
        width: 110,
        precision: 2,
        minValue: 0,
        parse: parseNullableNumber,
      }),
      MoneyCell<DealRowsGridRow, DealRowsGridPatch, DealRowsGridColumnKey>({
        key: 'expenses_amount',
        title: 'Сумма расходов',
        width: 130,
        value: row => {
          const rowId = Number(row.id ?? 0);
          const purchaseTotal = roundMoney(
            Number(row.purchase_price ?? 0) * Number(row.quantity ?? 0)
          );
          const workOrdersTotal = rowId > 0 ? (expensesByDealRowId[rowId] ?? 0) : 0;
          return roundMoney(purchaseTotal + workOrdersTotal);
        },
        editable: false,
      }),
      CheckboxCell<DealRowsGridRow, DealRowsGridPatch, DealRowsGridColumnKey>({
        key: 'approved',
        title: '✓',
        width: 40,
        getValue: row => !!row.approved_at,
        buildTogglePatch: (_row, checked) => ({
          apiPatch: { approved_at: checked ? new Date().toISOString() : null },
          localPatch: { approved_at: checked ? new Date().toISOString() : null },
        }),
      }),
    ],
    [expensesByDealRowId]
  );

  const loadRows = useCallback(async (): Promise<DealRowsGridRow[]> => {
    setError(null);
    try {
      const activeVersion = await dealVersionRepoRef.current.getActive(dealId);

      if (!activeVersion) {
        setVersion(null);
        rowsCacheRef.current = [];
        setTotals(EMPTY_TOTALS);
        return [];
      }

      setVersion({ id: activeVersion.id, version: activeVersion.version });
      const rows = (await dealRowRepoRef.current
        .filterByDealId(dealId, activeVersion.id)
        .fetch()) as DealRowsGridRow[];
      const normalized = rows ?? [];
      rowsCacheRef.current = normalized;
      setTotals(calculateRowsTotals(normalized));

      const dealRowIds = normalized
        .map(r => r.id)
        .filter((id): id is number => id != null && id > 0);
      void loadExpensesForRows(dealRowIds);

      return normalized;
    } catch (err) {
      const message = err instanceof Error ? err.message : String(err);
      setError(message);
      rowsCacheRef.current = [];
      setTotals(EMPTY_TOTALS);
      return [];
    }
  }, [dealId, loadExpensesForRows]);

  const createRow = useCallback(
    async (payload: CreateDealRow): Promise<DealRowsGridRow> => {
      const activeVersion = await dealVersionRepoRef.current.getActiveOrCreate(dealId);
      setVersion({ id: activeVersion.id, version: activeVersion.version });

      const quantity = Number(payload.quantity ?? 0);
      const price = Number(payload.price ?? 0);
      const normalizedPayload: CreateDealRow = {
        ...payload,
        deal_id: dealId,
        deal_version_id: activeVersion.id,
        product_name: payload.product_name?.trim() || 'Новая позиция',
        quantity,
        price,
        amount: calculateRowAmount({ quantity, price }),
        vat: Number(payload.vat ?? 0),
        metadata: payload.metadata ?? null,
      };

      const created = (await dealRowRepoRef.current.create(normalizedPayload)) as DealRowsGridRow;
      const nextRows = [...rowsCacheRef.current, created];
      rowsCacheRef.current = nextRows;
      setTotals(calculateRowsTotals(nextRows));
      return created;
    },
    [dealId]
  );

  const updateRow = useCallback(
    async (
      rowId: number,
      patch: DealRowsGridPatch
    ): Promise<{ localPatch: Partial<DealRowsGridRow> }> => {
      const effectivePatch: DealRowsGridPatch = { ...patch };

      await dealRowRepoRef.current.update(rowId, effectivePatch as UpdateDealRow);
      const nextRows = rowsCacheRef.current.map(row =>
        row.id === rowId ? { ...row, ...effectivePatch } : row
      );
      rowsCacheRef.current = nextRows;
      setTotals(calculateRowsTotals(nextRows));
      return { localPatch: effectivePatch };
    },
    []
  );

  const deleteRow = useCallback(async (rowId: number): Promise<void> => {
    await dealRowRepoRef.current.deleteById(rowId);
    const nextRows = rowsCacheRef.current.filter(row => row.id !== rowId);
    rowsCacheRef.current = nextRows;
    setTotals(calculateRowsTotals(nextRows));
    setExpensesByDealRowId(prev => {
      const next = { ...prev };
      delete next[rowId];
      return next;
    });
  }, []);

  const defaults = useMemo<CreateDealRow>(
    () => ({
      deal_id: dealId,
      deal_version_id: 0,
      product_id: null,
      product_name: 'Новая позиция',
      product_category: null,
      product_brand: null,
      product_supplier: null,
      supplier_id: null,
      quantity: 1,
      price: 0,
      amount: 0,
      vat: 0,
      purchase_price: null,
      metadata: null,
      notes: null,
      approved_at: null,
    }),
    [dealId]
  );

  const dataSource = useMemo(
    () => ({
      loadRows,
      createRow,
      updateRow,
      deleteRow,
      defaults,
    }),
    [createRow, defaults, deleteRow, loadRows, updateRow]
  );

  const handleWorkOrdersChanged = useCallback(
    (dealRowId: number) => {
      void refreshExpensesForDealRow(dealRowId);
    },
    [refreshExpensesForDealRow]
  );

  const handleNotesSaved = useCallback((rowId: number, notes: string | null) => {
    rowsCacheRef.current = rowsCacheRef.current.map(row =>
      row.id === rowId ? { ...row, notes } : row
    );
  }, []);

  const expandableConfig = useMemo(
    () => ({
      mode: 'single' as const,
      renderExpandedRow: ({ row, rowId }: { row: DealRowsGridRow; rowId: number }) => (
        <>
          <DealRowNotes
            dealRowId={rowId}
            value={row.notes ?? null}
            onSaved={notes => handleNotesSaved(rowId, notes)}
          />
          <DealRowWorkOrdersBlock dealRowId={rowId} onWorkOrdersChanged={handleWorkOrdersChanged} />
        </>
      ),
    }),
    [handleWorkOrdersChanged, handleNotesSaved]
  );

  const sortableConfig = useMemo(
    () => ({
      onReorder: async ({
        activeRowId,
        oldIndex,
        newIndex,
        rows,
        actions,
      }: {
        activeRowId: number;
        oldIndex: number;
        newIndex: number;
        rows: DealRowsGridRow[];
        actions: {
          replaceRows: (nextRows: DealRowsGridRow[]) => void;
        };
      }) => {
        const ordered = [...rows] as DealRowsGridRow[];
        const [moved] = ordered.splice(oldIndex, 1);
        if (!moved) return;
        ordered.splice(newIndex, 0, moved);

        const nextOrder = calculateNewSortOrder(ordered, newIndex);
        const optimisticRows = ordered.map(row =>
          row.id === activeRowId ? { ...row, sort_order: nextOrder } : row
        );

        const previousRows = [...rowsCacheRef.current];
        rowsCacheRef.current = optimisticRows;
        actions.replaceRows(optimisticRows);

        try {
          await dealRowRepoRef.current.setRowPosition(activeRowId, nextOrder);
        } catch (orderError) {
          rowsCacheRef.current = previousRows;
          actions.replaceRows(previousRows);
          throw orderError;
        }
      },
    }),
    []
  );

  const handleProductsSelect = useCallback(
    async (selectedProducts: SelectedProductItem[]) => {
      if (!selectedProducts.length) {
        closeProductPickerModal();
        return;
      }

      setError(null);

      try {
        for (const selected of selectedProducts) {
          const product = selected.product;
          const parsedProductId = Number(product.id);
          const productId =
            Number.isFinite(parsedProductId) && parsedProductId >= 0 ? parsedProductId : null;

          await createRow({
            ...defaults,
            product_id: productId,
            product_name: product.name?.trim() || 'Новая позиция',
            product_category: product.categories?.[0] ?? null,
            product_brand: product.brand_name?.trim() || null,
            quantity: Math.max(1, Math.round(Number(selected.quantity) || 1)),
            price: Number(product.minPrice ?? 0),
            amount: 0,
            metadata: (() => {
              const images = getProductMetadataImages(product);
              return images.length > 0 ? { images } : null;
            })(),
          });
        }

        setGridRevision(prev => prev + 1);
        closeProductPickerModal();
      } catch (err) {
        const message = err instanceof Error ? err.message : String(err);
        setError(message);
      }
    },
    [closeProductPickerModal, createRow, defaults]
  );

  const handleOpenCatalogPicker = useCallback(() => {
    openProductPickerModal(
      {
        title: 'Подбор товаров',
        onProductsSelect: (selectedProducts: SelectedProductItem[]) => {
          void handleProductsSelect(selectedProducts);
        },
        onClose: () => {
          closeProductPickerModal();
        },
      },
      {
        size: '90vw',
        centered: true,
      }
    );
  }, [closeProductPickerModal, handleProductsSelect, openProductPickerModal]);

  const handleShareProposal = useCallback(async () => {
    if (!dealUuid) {
      setError('UUID сделки не найден. Невозможно сформировать ссылку на КП.');
      return;
    }

    try {
      await copyTextToClipboard(buildProposalViewerLink(dealUuid));
      setShareCopied(true);
      if (shareResetTimerRef.current) {
        clearTimeout(shareResetTimerRef.current);
      }
      shareResetTimerRef.current = setTimeout(() => {
        setShareCopied(false);
        shareResetTimerRef.current = null;
      }, 1500);
    } catch (copyError) {
      const message =
        copyError instanceof Error ? copyError.message : 'Не удалось скопировать ссылку на КП';
      setError(message);
    }
  }, [dealUuid]);

  return (
    <Card padding='xs' shadow='sm'>
      <RowsHeader
        version={version}
        onOpenCatalogPicker={handleOpenCatalogPicker}
        onShareProposal={handleShareProposal}
        shareCopied={shareCopied}
        shareDisabled={!dealUuid}
      />

      {error && (
        <Alert color='red' title='Ошибка' mb='xs' maw='100%'>
          <Text size='xs'>{error}</Text>
        </Alert>
      )}

      <EditableGrid
        key={`deal-rows-grid-${dealId}-${gridRevision}`}
        persistId='deal-rows'
        columns={columns}
        getRowId={row => row.id}
        dataSource={dataSource}
        loadingText='Загрузка строк сделки...'
        emptyText='Нет строк в активной версии'
        tableMinWidth={1080}
        actionColumnWidth={48}
        appendEmptyRow
        expandable={expandableConfig}
        sortable={sortableConfig}
        renderRowActions={({ rowId, state, actions }) => (
          <Tooltip label='Удалить строку'>
            <ActionIcon
              size='xs'
              color='red'
              variant='subtle'
              disabled={state.rowSaving || state.creatingRow}
              onClick={() => {
                if (!confirm('Удалить эту строку?')) return;
                void actions.deleteRow(rowId);
              }}
            >
              <IconTrash size={12} />
            </ActionIcon>
          </Tooltip>
        )}
      />

      <RowsTotals totals={totals} />
    </Card>
  );
}

const RowsHeader = React.memo(function RowsHeader({
  version,
  onOpenCatalogPicker,
  onShareProposal,
  shareCopied,
  shareDisabled,
}: {
  version: { id: number; version: string } | null;
  onOpenCatalogPicker: () => void;
  onShareProposal: () => void;
  shareCopied: boolean;
  shareDisabled: boolean;
}) {
  return (
    <Group justify='space-between' mb='xs'>
      <Text fw={600} size='xs'>
        Позиции сделки
      </Text>
      <Group gap='xs'>
        <Button size='xs' variant='light' onClick={onOpenCatalogPicker}>
          Подбор товаров
        </Button>
        <Button
          size='xs'
          variant={shareCopied ? 'filled' : 'default'}
          color={shareCopied ? 'green' : undefined}
          onClick={onShareProposal}
          disabled={shareDisabled}
        >
          {shareCopied ? 'Скопировано' : 'Поделиться'}
        </Button>
        {version && (
          <Text size='xs' c='dimmed'>
            {version.version}
          </Text>
        )}
      </Group>
    </Group>
  );
});

const RowsTotals = React.memo(function RowsTotals({ totals }: { totals: DealRowsTotals }) {
  return (
    <Group justify='flex-end' mt='xs'>
      <Text size='xs' fw={700}>
        Итого: {totals.total.toLocaleString()}
      </Text>
    </Group>
  );
});

export default DealRowsPanel;
